import React, { useState, useEffect } from 'react';
import { Plus, Trash2, RotateCcw, AlertTriangle } from 'react-feather';
import { apiClient } from '../shared/api/client';
import { parseAmount } from '../shared/utils/amount';
import PageHeader from '../components/PageHeader';

interface Bucket {
  id: number;
  name: string;
  color?: string;
  monthlyAverage: number;
}

interface Scenario {
  id: number;
  name: string;
  monthlyCost: number;
  adjustments: { [key: string]: number };
}

interface AffordData {
  monthsCounted: number;
  avgIncome: number;
  avgExpense: number;
  headroom: number;
  worstMonth: number;
  negativeMonths: number;
  buckets: Bucket[];
  scenarios: Scenario[];
}

// Roughly what a net krona costs in gross salary at a normal Swedish
// marginal rate - enough to answer "how much more would I have to earn?"
const GROSS_FACTOR = 1.55;

// "Can I afford it?" - the consequence view. One bar answers the question:
// green is the room you have, red is what is missing. The sliders let you
// take the money from somewhere until it fits.
const AffordPage: React.FC = () => {
  const [data, setData] = useState<AffordData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('Bil');
  const [costInput, setCostInput] = useState('7000');
  const [cuts, setCuts] = useState<{ [id: number]: number }>({});
  const [extraIncome, setExtraIncome] = useState(0);
  const [saving, setSaving] = useState(false);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(amount)) + ' kr';

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/affordability');
      setData(response as any);
    } catch (err) {
      console.error('Load affordability error:', err);
      setError('Kunde inte hämta underlaget. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cost = parseAmount(costInput) || 0;
  const freed = Object.values(cuts).reduce((s, v) => s + v, 0) + extraIncome;
  const headroom = (data?.headroom || 0) + freed;
  const gap = Math.max(cost - headroom, 0);
  const fits = gap <= 0;

  // The bar is scaled to whichever is bigger: the room or the cost. Then
  // green is always "what I have" and red is always "what is missing".
  const scale = Math.max(cost, headroom, 1);
  const greenPct = Math.min((Math.min(headroom, cost) / scale) * 100, 100);
  const leftoverPct = fits ? ((headroom - cost) / scale) * 100 : 0;
  const gapPct = (gap / scale) * 100;

  const resetLevers = () => { setCuts({}); setExtraIncome(0); };

  const saveScenario = async () => {
    if (!name.trim() || cost <= 0) return;
    try {
      setSaving(true);
      await apiClient.post('/affordability/scenarios', {
        name: name.trim(),
        monthlyCost: cost,
        adjustments: { ...cuts, ...(extraIncome ? { income: extraIncome } : {}) },
      });
      await load();
    } catch (err) {
      console.error('Save scenario error:', err);
      setError('Scenariot kunde inte sparas.');
    } finally {
      setSaving(false);
    }
  };

  const openScenario = (s: Scenario) => {
    setName(s.name);
    setCostInput(String(s.monthlyCost));
    const adj = { ...(s.adjustments || {}) } as any;
    setExtraIncome(adj.income || 0);
    delete adj.income;
    const restored: { [id: number]: number } = {};
    Object.keys(adj).forEach(k => { restored[Number(k)] = adj[k]; });
    setCuts(restored);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteScenario = async (s: Scenario) => {
    if (!window.confirm(`Ta bort scenariot "${s.name}"?`)) return;
    try {
      await apiClient.delete(`/affordability/scenarios/${s.id}`);
      await load();
    } catch (err) {
      console.error('Delete scenario error:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader mode="title" title="Har jag råd?" />
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader mode="title" title="Har jag råd?" />
        <div className="page-content px-4 py-6">
          <div className="bg-white rounded-xl p-6 shadow-sm text-center">
            <AlertTriangle size={40} className="mx-auto mb-3 text-red-400" />
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={load} className="bg-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold">Försök igen</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader mode="title" title="Har jag råd?" />
      <div className="page-content px-4 py-6">

        {/* What is being tested */}
        <div className="bg-white rounded-xl p-5 mb-4 shadow-sm">
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Vad? T.ex. Bil"
              className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-base"
            />
            <div className="relative w-36 flex-shrink-0">
              <input
                type="text"
                inputMode="decimal"
                value={costInput}
                onChange={e => setCostInput(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-3 pr-14 py-2 text-base text-right font-semibold"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">kr/mån</span>
            </div>
          </div>

          {/* The answer, as one bar: green is what you have, red is what is
              missing. No percentages, no table. */}
          <p className={`text-3xl font-bold mb-1 ${fits ? 'text-green-600' : 'text-red-600'}`}>
            {fits ? 'Det går ihop' : `${formatAmount(gap)} saknas`}
          </p>
          <p className="text-sm text-gray-500 mb-3">
            {fits
              ? `${formatAmount(headroom - cost)} kvar varje månad efter ${name.trim() || 'kostnaden'}`
              : `${name.trim() || 'Kostnaden'} kostar ${formatAmount(cost)} — du har ${formatAmount(headroom)}`}
          </p>

          <div className="h-8 rounded-full overflow-hidden flex bg-gray-100">
            <div className="h-full bg-green-500" style={{ width: `${greenPct}%` }}></div>
            {fits
              ? <div className="h-full bg-green-200" style={{ width: `${leftoverPct}%` }}></div>
              : <div
                  className="h-full"
                  style={{
                    width: `${gapPct}%`,
                    backgroundImage: 'repeating-linear-gradient(45deg, #FCA5A5 0 8px, #FECACA 8px 12px)'
                  }}
                ></div>}
          </div>
          <div className="flex justify-between mt-1.5 text-xs">
            <span className="text-green-700 font-medium">{formatAmount(Math.min(headroom, cost))} du har</span>
            {fits
              ? <span className="text-gray-500">{formatAmount(headroom - cost)} över</span>
              : <span className="text-red-600 font-medium">{formatAmount(gap)} saknas</span>}
          </div>
        </div>

        {/* Where the money would come from */}
        <div className="bg-white rounded-xl p-5 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold text-gray-900">Var ska pengarna tas?</h2>
            {freed > 0 && (
              <button onClick={resetLevers} className="inline-flex items-center text-xs font-medium text-blue-600">
                <RotateCcw size={12} className="mr-1" />Nollställ
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-4">Dra i reglagen tills stapeln ovan blir grön.</p>

          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm font-medium text-gray-800">Högre lön</span>
              <span className="text-sm font-semibold text-gray-900">
                {extraIncome > 0 ? `+${formatAmount(extraIncome)}` : '—'}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={10000}
              step={500}
              value={extraIncome}
              onChange={e => setExtraIncome(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
            {extraIncome > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">
                ungefär {formatAmount(extraIncome * GROSS_FACTOR)} före skatt
              </p>
            )}
          </div>

          {(data?.buckets || []).slice(0, 6).map(b => {
            const cut = cuts[b.id] || 0;
            return (
              <div key={b.id} className="mb-4 last:mb-0">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="inline-flex items-center text-sm font-medium text-gray-800">
                    <span className="w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: b.color || '#9CA3AF' }}></span>
                    {b.name}
                  </span>
                  <span className="text-sm text-gray-500">
                    {cut > 0
                      ? <><span className="line-through mr-1.5">{formatAmount(b.monthlyAverage)}</span><span className="font-semibold text-gray-900">{formatAmount(b.monthlyAverage - cut)}</span></>
                      : <span className="font-semibold text-gray-900">{formatAmount(b.monthlyAverage)}</span>}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={b.monthlyAverage}
                  step={100}
                  value={cut}
                  onChange={e => setCuts(prev => ({ ...prev, [b.id]: Number(e.target.value) }))}
                  className="w-full accent-blue-600"
                />
              </div>
            );
          })}
        </div>

        {/* The ground truth this rests on */}
        <div className="bg-white rounded-xl p-5 mb-4 shadow-sm">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-500">Normalt över i månaden</span>
            <span className="text-xl font-bold text-gray-900">{formatAmount(data?.headroom || 0)}</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {formatAmount(data?.avgIncome || 0)} in och {formatAmount(data?.avgExpense || 0)} ut i snitt över {data?.monthsCounted} månader
          </p>
          {(data?.negativeMonths || 0) > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-2">
              {data?.negativeMonths} av {data?.monthsCounted} månader gick back, sämsta med {formatAmount(Math.abs(data?.worstMonth || 0))}.
              En fast kostnad känns hårdast just de månaderna.
            </p>
          )}
        </div>

        <button
          onClick={saveScenario}
          disabled={saving || !name.trim() || cost <= 0}
          className="w-full bg-white border-2 border-dashed border-blue-600 text-blue-600 py-3.5 rounded-xl font-semibold hover:bg-blue-50 transition-colors disabled:opacity-50 mb-4"
        >
          <Plus size={16} className="inline mr-1 -mt-0.5" />{saving ? 'Sparar…' : 'Spara som scenario'}
        </button>

        {(data?.scenarios || []).length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-500 mb-2 px-1">Sparade scenarier</h2>
            {(data?.scenarios || []).map(s => {
              const sFreed = Object.entries(s.adjustments || {}).reduce((sum, [, v]) => sum + (Number(v) || 0), 0);
              const sHeadroom = (data?.headroom || 0) + sFreed;
              const sGap = Math.max(s.monthlyCost - sHeadroom, 0);
              return (
                <div key={s.id} className="bg-white rounded-xl p-4 mb-3 shadow-sm flex items-center justify-between">
                  <button onClick={() => openScenario(s)} className="flex-1 text-left min-w-0 mr-3">
                    <p className="text-base font-semibold text-gray-900 truncate">{s.name}</p>
                    <p className="text-xs text-gray-500">
                      {formatAmount(s.monthlyCost)}/mån · {sGap > 0
                        ? <span className="text-red-600 font-medium">{formatAmount(sGap)} saknas</span>
                        : <span className="text-green-600 font-medium">går ihop</span>}
                    </p>
                  </button>
                  <button onClick={() => deleteScenario(s)} className="text-gray-400 hover:text-red-500 p-2 flex-shrink-0">
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AffordPage;
